import { useEffect, useLayoutEffect, useRef } from "react";
import { useFormApi } from "../useFormApi.js";
function composeValidationId(errorId) {
  return `${errorId}-validation`;
}
function useAddFieldValidation(fieldName, validateField, options = {}) {
  const initialFieldName = useRef(fieldName);
  if (fieldName !== initialFieldName.current) {
    throw new Error('"fieldName" cannot change');
  }
  const {
    customUID,
    disabled,
    message,
    onUnmountBehavior = "clear-errors",
    priority,
    validationTrigger = "field"
  } = options;
  const formApi = useFormApi();
  const rOnUnmountBehavior = useRef(onUnmountBehavior);
  useLayoutEffect(() => void (rOnUnmountBehavior.current = onUnmountBehavior), [onUnmountBehavior]);
  const rDefaultValidationUID = useRef(`${fieldName.toString()}-field-v-${Math.random()}`);
  const rValidationUID = useRef(customUID ?? rDefaultValidationUID.current);
  useLayoutEffect(() => void (rValidationUID.current = customUID ?? rDefaultValidationUID.current), [customUID]);
  useEffect(() => {
    customUID;
    if (disabled) return;
    const errorId = rValidationUID.current;
    const validationId = composeValidationId(errorId);
    const validator = createFieldValidation(errorId, fieldName, validateField, {
      disabled,
      message,
      priority
    });
    const targetValidationField = validationTrigger === "field" ? fieldName : void 0;
    formApi.addValidation(validationId, validator, targetValidationField);
    return () => {
      formApi.removeValidation(validationId);
    };
  }, [formApi, customUID, disabled, fieldName, message, priority, validateField, validationTrigger]);
  useEffect(() => {
    return () => {
      if (rOnUnmountBehavior.current === "clear-errors") {
        formApi.clearError(rValidationUID.current);
      }
    };
  }, [formApi]);
  return {
    errorId: rValidationUID.current,
    formApi,
    validationId: composeValidationId(rValidationUID.current)
  };
}
function createFieldValidation(uid, fieldName, validateField, options = {}) {
  const { message = `The field "${fieldName}" is invalid.`, priority } = options;
  function createReturnValue(field, validationResult, formApi) {
    const errorMessage = typeof validationResult === "string" ? validationResult : typeof message === "function" ? message(fieldName, field, formApi) : message;
    const hasError = !!validationResult;
    return {
      [uid]: hasError ? {
        id: uid,
        field: fieldName,
        message: errorMessage,
        priority
      } : null
    };
  }
  return (formApi) => {
    const field = formApi.getField(fieldName);
    const result = validateField(field, formApi);
    if (result instanceof Promise) {
      return result.then((result2) => {
        return createReturnValue(field, result2, formApi);
      });
    }
    return createReturnValue(field, result, formApi);
  };
}
export {
  createFieldValidation,
  useAddFieldValidation
};
//# sourceMappingURL=useAddFieldValidation.js.map
